import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function BranchManagement() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("branches");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Data states
  const [branches, setBranches] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchUsers, setBranchUsers] = useState([]);
  const [branchLedger, setBranchLedger] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [users, setUsers] = useState([]);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [showFloatModal, setShowFloatModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showJournalModal, setShowJournalModal] = useState(false);

  // Form states
  const [branchForm, setBranchForm] = useState({
    code: "", name: "", address: "", city: "", state: "",
    phone: "", email: "", managerId: "", openingBalance: ""
  });
  const [assignForm, setAssignForm] = useState({ userId: "", branchRole: "Staff", isPrimaryBranch: true });
  const [floatForm, setFloatForm] = useState({ amount: "", description: "", reference: "", type: "allocate" });
  const [transferForm, setTransferForm] = useState({ fromBranchId: "", toBranchId: "", amount: "", description: "" });
  const [journalForm, setJournalForm] = useState({
    description: "", reference: "",
    lines: [{ accountCode: "", debit: "", credit: "", description: "" }]
  });

  const [processing, setProcessing] = useState(false);

  // Load data
  const loadBranches = useCallback(async () => {
    try {
      const res = await api.get("/Branch");
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to load branches:", err);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const res = await api.get("/Branch/summary");
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to load summary:", err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get("/Admin/users");
      setUsers(res.data);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  }, []);

  const loadBranchUsers = async (branchId) => {
    try {
      const res = await api.get(`/Branch/${branchId}/users`);
      setBranchUsers(res.data);
    } catch (err) {
      console.error("Failed to load branch users:", err);
    }
  };

  const loadBranchLedger = async (branchId) => {
    try {
      const res = await api.get(`/Branch/${branchId}/ledger-accounts`);
      setBranchLedger(res.data);
    } catch (err) {
      console.error("Failed to load branch ledger:", err);
    }
  };

  const loadJournalEntries = async (branchId) => {
    try {
      const res = await api.get(`/Branch/${branchId}/journal-entries`);
      setJournalEntries(res.data);
    } catch (err) {
      console.error("Failed to load journal entries:", err);
    }
  };

  const loadTransfers = useCallback(async () => {
    try {
      const res = await api.get("/Branch/transfers");
      setTransfers(res.data);
    } catch (err) {
      console.error("Failed to load transfers:", err);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadBranches(), loadSummary(), loadUsers(), loadTransfers()]);
      setLoading(false);
    };
    loadData();
  }, [loadBranches, loadSummary, loadUsers, loadTransfers]);

  useEffect(() => {
    if (selectedBranch) {
      loadBranchUsers(selectedBranch.id);
      loadBranchLedger(selectedBranch.id);
      loadJournalEntries(selectedBranch.id);
    }
  }, [selectedBranch]);

  // Handlers
  const handleCreateBranch = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await api.post("/Branch", {
        ...branchForm,
        managerId: branchForm.managerId ? parseInt(branchForm.managerId) : null,
        openingBalance: parseFloat(branchForm.openingBalance) || 0
      });
      alert("Branch created successfully!");
      setShowCreateModal(false);
      setBranchForm({ code: "", name: "", address: "", city: "", state: "", phone: "", email: "", managerId: "", openingBalance: "" });
      loadBranches();
      loadSummary();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create branch");
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateBranch = async (e) => {
    e.preventDefault();
    if (!selectedBranch) return;
    setProcessing(true);
    try {
      await api.put(`/Branch/${selectedBranch.id}`, {
        ...branchForm,
        managerId: branchForm.managerId ? parseInt(branchForm.managerId) : null
      });
      alert("Branch updated successfully!");
      setShowEditModal(false);
      loadBranches();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update branch");
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm("Are you sure you want to deactivate this branch?")) return;
    try {
      await api.delete(`/Branch/${branchId}`);
      alert("Branch deactivated successfully!");
      loadBranches();
      loadSummary();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete branch");
    }
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    if (!selectedBranch) return;
    setProcessing(true);
    try {
      await api.post(`/Branch/${selectedBranch.id}/users`, {
        userId: parseInt(assignForm.userId),
        branchRole: assignForm.branchRole,
        isPrimaryBranch: assignForm.isPrimaryBranch
      });
      alert("User assigned successfully!");
      setShowAssignUserModal(false);
      setAssignForm({ userId: "", branchRole: "Staff", isPrimaryBranch: true });
      loadBranchUsers(selectedBranch.id);
      loadBranches();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to assign user");
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!selectedBranch || !window.confirm("Remove this user from the branch?")) return;
    try {
      await api.delete(`/Branch/${selectedBranch.id}/users/${userId}`);
      alert("User removed from branch!");
      loadBranchUsers(selectedBranch.id);
      loadBranches();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to remove user");
    }
  };

  const handleFloatOperation = async (e) => {
    e.preventDefault();
    if (!selectedBranch) return;
    setProcessing(true);
    try {
      const endpoint = floatForm.type === "allocate" 
        ? `/Branch/${selectedBranch.id}/float/allocate`
        : `/Branch/${selectedBranch.id}/float/recall`;
      
      const res = await api.post(endpoint, {
        amount: parseFloat(floatForm.amount),
        description: floatForm.description,
        reference: floatForm.reference
      });
      
      alert(res.data.message);
      setShowFloatModal(false);
      setFloatForm({ amount: "", description: "", reference: "", type: "allocate" });
      loadBranches();
      loadSummary();
      loadBranchLedger(selectedBranch.id);
    } catch (err) {
      alert(err.response?.data?.message || "Float operation failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateTransfer = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await api.post("/Branch/transfers", {
        fromBranchId: parseInt(transferForm.fromBranchId),
        toBranchId: parseInt(transferForm.toBranchId),
        amount: parseFloat(transferForm.amount),
        description: transferForm.description
      });
      alert("Transfer request created!");
      setShowTransferModal(false);
      setTransferForm({ fromBranchId: "", toBranchId: "", amount: "", description: "" });
      loadTransfers();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create transfer");
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveTransfer = async (transferId) => {
    if (!window.confirm("Approve this transfer?")) return;
    try {
      const res = await api.post(`/Branch/transfers/${transferId}/approve`);
      alert(res.data.message);
      loadTransfers();
      loadBranches();
      loadSummary();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to approve transfer");
    }
  };

  const handleCreateJournalEntry = async (e) => {
    e.preventDefault();
    if (!selectedBranch) return;
    
    const lines = journalForm.lines.filter(l => l.accountCode && (l.debit || l.credit));
    const totalDebit = lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
    const totalCredit = lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
    
    if (totalDebit !== totalCredit) {
      alert(`Debits (₦${totalDebit.toLocaleString()}) must equal Credits (₦${totalCredit.toLocaleString()})`);
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/Branch/${selectedBranch.id}/journal-entries`, {
        description: journalForm.description,
        reference: journalForm.reference,
        lines: lines.map(l => ({
          accountCode: l.accountCode,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
          description: l.description
        }))
      });
      alert("Journal entry created!");
      setShowJournalModal(false);
      setJournalForm({ description: "", reference: "", lines: [{ accountCode: "", debit: "", credit: "", description: "" }] });
      loadBranchLedger(selectedBranch.id);
      loadJournalEntries(selectedBranch.id);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create journal entry");
    } finally {
      setProcessing(false);
    }
  };

  const addJournalLine = () => {
    setJournalForm({
      ...journalForm,
      lines: [...journalForm.lines, { accountCode: "", debit: "", credit: "", description: "" }]
    });
  };

  const removeJournalLine = (index) => {
    setJournalForm({
      ...journalForm,
      lines: journalForm.lines.filter((_, i) => i !== index)
    });
  };

  const updateJournalLine = (index, field, value) => {
    const newLines = [...journalForm.lines];
    newLines[index][field] = value;
    setJournalForm({ ...journalForm, lines: newLines });
  };

  const openEditModal = (branch) => {
    setSelectedBranch(branch);
    setBranchForm({
      code: branch.code,
      name: branch.name,
      address: branch.address || "",
      city: branch.city || "",
      state: branch.state || "",
      phone: branch.phone || "",
      email: branch.email || "",
      managerId: branch.managerId || "",
      openingBalance: ""
    });
    setShowEditModal(true);
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString()}`;

  const getAccountTypeColor = (type) => {
    const colors = {
      Asset: "#10b981",
      Liability: "#ef4444",
      Equity: "#8b5cf6",
      Revenue: "#3b82f6",
      Expense: "#f59e0b"
    };
    return colors[type] || "#6b7280";
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading branch data...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate(-1)} style={styles.backBtn}>← Back</button>
        <h1 style={styles.title}>🏢 Branch Management</h1>
        <div style={styles.headerActions}>
          <button onClick={() => setShowTransferModal(true)} style={styles.transferBtn}>
            🔄 Inter-Branch Transfer
          </button>
          <button onClick={() => setShowCreateModal(true)} style={styles.createBtn}>
            + Create Branch
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={styles.summaryGrid}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>🏢</div>
            <div style={styles.summaryContent}>
              <div style={styles.summaryValue}>{summary.totals.totalBranches}</div>
              <div style={styles.summaryLabel}>Total Branches</div>
            </div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>💰</div>
            <div style={styles.summaryContent}>
              <div style={styles.summaryValue}>{formatCurrency(summary.totals.totalFloat)}</div>
              <div style={styles.summaryLabel}>Total Float</div>
            </div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>📈</div>
            <div style={styles.summaryContent}>
              <div style={styles.summaryValue}>{formatCurrency(summary.totals.totalRevenue)}</div>
              <div style={styles.summaryLabel}>Total Revenue</div>
            </div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryIcon}>💵</div>
            <div style={styles.summaryContent}>
              <div style={{ ...styles.summaryValue, color: summary.totals.netIncome >= 0 ? "#10b981" : "#ef4444" }}>
                {formatCurrency(summary.totals.netIncome)}
              </div>
              <div style={styles.summaryLabel}>Net Income</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        <button 
          onClick={() => setActiveTab("branches")} 
          style={{ ...styles.tab, ...(activeTab === "branches" ? styles.tabActive : {}) }}
        >
          🏢 Branches
        </button>
        <button 
          onClick={() => setActiveTab("transfers")} 
          style={{ ...styles.tab, ...(activeTab === "transfers" ? styles.tabActive : {}) }}
        >
          🔄 Transfers
        </button>
        {selectedBranch && (
          <>
            <button 
              onClick={() => setActiveTab("users")} 
              style={{ ...styles.tab, ...(activeTab === "users" ? styles.tabActive : {}) }}
            >
              👥 {selectedBranch.name} Users
            </button>
            <button 
              onClick={() => setActiveTab("ledger")} 
              style={{ ...styles.tab, ...(activeTab === "ledger" ? styles.tabActive : {}) }}
            >
              📒 {selectedBranch.name} Ledger
            </button>
            <button 
              onClick={() => setActiveTab("journals")} 
              style={{ ...styles.tab, ...(activeTab === "journals" ? styles.tabActive : {}) }}
            >
              📝 Journals
            </button>
          </>
        )}
      </div>

      {/* Branches Tab */}
      {activeTab === "branches" && (
        <div style={styles.content}>
          <div style={styles.branchListContainer}>
            <table style={styles.branchTable}>
              <thead>
                <tr>
                  <th style={styles.branchTh}>Branch Name</th>
                  <th style={styles.branchTh}>Status</th>
                  <th style={styles.branchTh}>Float Balance</th>
                  <th style={styles.branchTh}>Users</th>
                  <th style={styles.branchTh}>Manager</th>
                  <th style={styles.branchTh}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {branches.map(branch => (
                  <tr 
                    key={branch.id} 
                    style={{
                      ...styles.branchRow,
                      ...(selectedBranch?.id === branch.id ? styles.branchRowSelected : {}),
                      backgroundColor: branch.isHeadOffice ? "#fef9c3" : "white"
                    }}
                    onClick={() => setSelectedBranch(branch)}
                  >
                    <td style={styles.branchTd}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <span style={{ fontSize: "20px" }}>🏢</span>
                        <div>
                          <div style={{ fontWeight: "600", color: "#1f2937", cursor: "pointer" }}>
                            {branch.name}
                            {branch.isHeadOffice && <span style={styles.hqBadge}>HQ</span>}
                          </div>
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            {branch.code} • {branch.city}, {branch.state}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.branchTd}>
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "500",
                        backgroundColor: branch.isActive ? "#d1fae5" : "#fee2e2",
                        color: branch.isActive ? "#065f46" : "#991b1b"
                      }}>
                        {branch.isActive ? "✓ Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={styles.branchTd}>
                      <span style={{ fontWeight: "600", color: "#10b981" }}>
                        {formatCurrency(branch.currentBalance)}
                      </span>
                    </td>
                    <td style={styles.branchTd}>
                      <span>{branch.userCount} users</span>
                      <span style={{ color: "#6b7280", marginLeft: "5px" }}>
                        ({branch.agentCount} agents)
                      </span>
                    </td>
                    <td style={styles.branchTd}>
                      {branch.managerName || <span style={{ color: "#9ca3af" }}>Not assigned</span>}
                    </td>
                    <td style={styles.branchTd}>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button 
                          onClick={(e) => { e.stopPropagation(); openEditModal(branch); }} 
                          style={styles.tableActionBtn}
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setSelectedBranch(branch); setShowFloatModal(true); }} 
                          style={{ ...styles.tableActionBtn, backgroundColor: "#10b981", color: "white" }}
                        >
                          💰 Float
                        </button>
                        {!branch.isHeadOffice && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleDeleteBranch(branch.id); }} 
                            style={{ ...styles.tableActionBtn, backgroundColor: "#ef4444", color: "white" }}
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transfers Tab */}
      {activeTab === "transfers" && (
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3>Inter-Branch Transfers</h3>
              <button onClick={() => setShowTransferModal(true)} style={styles.smallBtn}>
                + New Transfer
              </button>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Transfer #</th>
                  <th style={styles.th}>From</th>
                  <th style={styles.th}>To</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Requested By</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transfers.length === 0 ? (
                  <tr><td colSpan="8" style={{ ...styles.td, textAlign: "center" }}>No transfers found</td></tr>
                ) : transfers.map(transfer => (
                  <tr key={transfer.id}>
                    <td style={styles.td}>{transfer.transferNumber}</td>
                    <td style={styles.td}>{transfer.fromBranch}</td>
                    <td style={styles.td}>{transfer.toBranch}</td>
                    <td style={{ ...styles.td, fontWeight: "600" }}>{formatCurrency(transfer.amount)}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: transfer.status === "Completed" ? "#d1fae5" :
                                        transfer.status === "Pending" ? "#fef3c7" : "#fee2e2",
                        color: transfer.status === "Completed" ? "#065f46" :
                               transfer.status === "Pending" ? "#92400e" : "#991b1b"
                      }}>
                        {transfer.status}
                      </span>
                    </td>
                    <td style={styles.td}>{transfer.requestedBy}</td>
                    <td style={styles.td}>{new Date(transfer.requestedAt).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      {transfer.status === "Pending" && (
                        <button 
                          onClick={() => handleApproveTransfer(transfer.id)}
                          style={{ ...styles.smallBtn, backgroundColor: "#10b981" }}
                        >
                          ✓ Approve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Branch Users Tab */}
      {activeTab === "users" && selectedBranch && (
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3>👥 {selectedBranch.name} - Staff</h3>
              <button onClick={() => setShowAssignUserModal(true)} style={styles.smallBtn}>
                + Assign User
              </button>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Primary Branch</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Assigned</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {branchUsers.length === 0 ? (
                  <tr><td colSpan="7" style={{ ...styles.td, textAlign: "center" }}>No users assigned</td></tr>
                ) : branchUsers.map(user => (
                  <tr key={user.id}>
                    <td style={styles.td}>
                      {user.fullName}
                      {user.isAgent && <span style={styles.agentBadge}>Agent</span>}
                    </td>
                    <td style={styles.td}>{user.email}</td>
                    <td style={styles.td}>
                      <span style={styles.roleBadge}>{user.branchRole}</span>
                    </td>
                    <td style={styles.td}>{user.isPrimaryBranch ? "✓ Yes" : "No"}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: user.isActive ? "#d1fae5" : "#fee2e2",
                        color: user.isActive ? "#065f46" : "#991b1b"
                      }}>
                        {user.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={styles.td}>{new Date(user.assignedAt).toLocaleDateString()}</td>
                    <td style={styles.td}>
                      <button 
                        onClick={() => handleRemoveUser(user.userId)}
                        style={{ ...styles.smallBtn, backgroundColor: "#ef4444" }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Branch Ledger Tab */}
      {activeTab === "ledger" && selectedBranch && (
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3>📒 {selectedBranch.name} - Chart of Accounts</h3>
              <button onClick={() => setShowJournalModal(true)} style={styles.smallBtn}>
                + Journal Entry
              </button>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Account #</th>
                  <th style={styles.th}>Account Name</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Category</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {branchLedger.map(account => (
                  <tr key={account.id}>
                    <td style={styles.td}>{account.accountCode}</td>
                    <td style={styles.td}>{account.accountName}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.typeBadge,
                        backgroundColor: getAccountTypeColor(account.accountType),
                        color: "white"
                      }}>
                        {account.accountType}
                      </span>
                    </td>
                    <td style={styles.td}>{account.accountCategory}</td>
                    <td style={{ ...styles.td, textAlign: "right", fontWeight: "600" }}>
                      {formatCurrency(account.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: "#f3f4f6" }}>
                  <td colSpan="4" style={{ ...styles.td, fontWeight: "600" }}>Total Assets</td>
                  <td style={{ ...styles.td, textAlign: "right", fontWeight: "600", color: "#10b981" }}>
                    {formatCurrency(branchLedger.filter(a => a.accountType === "Asset").reduce((s, a) => s + a.balance, 0))}
                  </td>
                </tr>
                <tr style={{ backgroundColor: "#f3f4f6" }}>
                  <td colSpan="4" style={{ ...styles.td, fontWeight: "600" }}>Total Liabilities</td>
                  <td style={{ ...styles.td, textAlign: "right", fontWeight: "600", color: "#ef4444" }}>
                    {formatCurrency(branchLedger.filter(a => a.accountType === "Liability").reduce((s, a) => s + a.balance, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Journal Entries Tab */}
      {activeTab === "journals" && selectedBranch && (
        <div style={styles.content}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3>📝 {selectedBranch.name} - Journal Entries</h3>
              <button onClick={() => setShowJournalModal(true)} style={styles.smallBtn}>
                + New Entry
              </button>
            </div>
            {journalEntries.length === 0 ? (
              <p style={{ textAlign: "center", padding: "20px", color: "#6b7280" }}>No journal entries found</p>
            ) : journalEntries.map(entry => (
              <div key={entry.id} style={styles.journalEntry}>
                <div style={styles.journalHeader}>
                  <div>
                    <strong>{entry.journalNumber}</strong>
                    <span style={styles.journalDate}>{new Date(entry.entryDate).toLocaleDateString()}</span>
                  </div>
                  <span style={styles.journalStatus}>{entry.status}</span>
                </div>
                <p style={styles.journalDesc}>{entry.description}</p>
                <table style={{ ...styles.table, fontSize: "12px", marginTop: "10px" }}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Account</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Debit</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.lines?.map((line, i) => (
                      <tr key={i}>
                        <td style={styles.td}>{line.accountCode} - {line.accountName}</td>
                        <td style={{ ...styles.td, textAlign: "right", color: "#10b981" }}>
                          {line.debit > 0 ? formatCurrency(line.debit) : ""}
                        </td>
                        <td style={{ ...styles.td, textAlign: "right", color: "#ef4444" }}>
                          {line.credit > 0 ? formatCurrency(line.credit) : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Branch Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>🏢 Create New Branch</h2>
            <form onSubmit={handleCreateBranch}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label>Branch Code *</label>
                  <input
                    type="number"
                    value={branchForm.code}
                    onChange={(e) => setBranchForm({ ...branchForm, code: e.target.value })}
                    placeholder="e.g., 001"
                    style={styles.input}
                    required
                    min="1"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Branch Name *</label>
                  <input
                    type="text"
                    value={branchForm.name}
                    onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })}
                    placeholder="e.g., Lagos Main Branch"
                    style={styles.input}
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Address</label>
                  <input
                    type="text"
                    value={branchForm.address}
                    onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })}
                    placeholder="Street address"
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>City</label>
                  <input
                    type="text"
                    value={branchForm.city}
                    onChange={(e) => setBranchForm({ ...branchForm, city: e.target.value })}
                    placeholder="City"
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>State</label>
                  <input
                    type="text"
                    value={branchForm.state}
                    onChange={(e) => setBranchForm({ ...branchForm, state: e.target.value })}
                    placeholder="State"
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Phone</label>
                  <input
                    type="text"
                    value={branchForm.phone}
                    onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })}
                    placeholder="Phone number"
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={branchForm.email}
                    onChange={(e) => setBranchForm({ ...branchForm, email: e.target.value })}
                    placeholder="branch@email.com"
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Branch Manager</label>
                  <select
                    value={branchForm.managerId}
                    onChange={(e) => setBranchForm({ ...branchForm, managerId: e.target.value })}
                    style={styles.input}
                  >
                    <option value="">Select Manager</option>
                    {users.filter(u => !u.isAgent).map(user => (
                      <option key={user.id} value={user.id}>{user.fullName}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label>Opening Balance (₦)</label>
                  <input
                    type="number"
                    value={branchForm.openingBalance}
                    onChange={(e) => setBranchForm({ ...branchForm, openingBalance: e.target.value })}
                    placeholder="0"
                    style={styles.input}
                  />
                </div>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowCreateModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={processing} style={styles.submitBtn}>
                  {processing ? "Creating..." : "Create Branch"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Branch Modal */}
      {showEditModal && selectedBranch && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>✏️ Edit Branch - {selectedBranch.code}</h2>
            <form onSubmit={handleUpdateBranch}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label>Branch Code *</label>
                  <input
                    type="number"
                    value={branchForm.code}
                    onChange={(e) => setBranchForm({ ...branchForm, code: e.target.value })}
                    style={styles.input}
                    required
                    min="1"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Branch Name</label>
                  <input
                    type="text"
                    value={branchForm.name}
                    onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Address</label>
                  <input
                    type="text"
                    value={branchForm.address}
                    onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>City</label>
                  <input
                    type="text"
                    value={branchForm.city}
                    onChange={(e) => setBranchForm({ ...branchForm, city: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>State</label>
                  <input
                    type="text"
                    value={branchForm.state}
                    onChange={(e) => setBranchForm({ ...branchForm, state: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Phone</label>
                  <input
                    type="text"
                    value={branchForm.phone}
                    onChange={(e) => setBranchForm({ ...branchForm, phone: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={branchForm.email}
                    onChange={(e) => setBranchForm({ ...branchForm, email: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label>Branch Manager</label>
                  <select
                    value={branchForm.managerId}
                    onChange={(e) => setBranchForm({ ...branchForm, managerId: e.target.value })}
                    style={styles.input}
                  >
                    <option value="">Select Manager</option>
                    {users.filter(u => !u.isAgent).map(user => (
                      <option key={user.id} value={user.id}>{user.fullName}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowEditModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={processing} style={styles.submitBtn}>
                  {processing ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign User Modal */}
      {showAssignUserModal && selectedBranch && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>👥 Assign User to {selectedBranch.name}</h2>
            <form onSubmit={handleAssignUser}>
              <div style={styles.formGroup}>
                <label>Select User *</label>
                <select
                  value={assignForm.userId}
                  onChange={(e) => setAssignForm({ ...assignForm, userId: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="">Select User</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.fullName} ({user.email}) {user.isAgent ? "- Agent" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label>Branch Role *</label>
                <select
                  value={assignForm.branchRole}
                  onChange={(e) => setAssignForm({ ...assignForm, branchRole: e.target.value })}
                  style={styles.input}
                >
                  <option value="Staff">Staff</option>
                  <option value="Teller">Teller</option>
                  <option value="Manager">Manager</option>
                  <option value="Agent">Agent</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    checked={assignForm.isPrimaryBranch}
                    onChange={(e) => setAssignForm({ ...assignForm, isPrimaryBranch: e.target.checked })}
                  />
                  Set as Primary Branch
                </label>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowAssignUserModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={processing} style={styles.submitBtn}>
                  {processing ? "Assigning..." : "Assign User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Float Operation Modal */}
      {showFloatModal && selectedBranch && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>💰 Float Operation - {selectedBranch.name}</h2>
            <p style={{ color: "#6b7280", marginBottom: "15px" }}>
              Current Balance: <strong>{formatCurrency(selectedBranch.currentBalance)}</strong>
            </p>
            <form onSubmit={handleFloatOperation}>
              <div style={styles.formGroup}>
                <label>Operation Type *</label>
                <div style={{ display: "flex", gap: "20px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="radio"
                      checked={floatForm.type === "allocate"}
                      onChange={() => setFloatForm({ ...floatForm, type: "allocate" })}
                    />
                    Allocate (Add Float)
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="radio"
                      checked={floatForm.type === "recall"}
                      onChange={() => setFloatForm({ ...floatForm, type: "recall" })}
                    />
                    Recall (Remove Float)
                  </label>
                </div>
              </div>
              <div style={styles.formGroup}>
                <label>Amount (₦) *</label>
                <input
                  type="number"
                  value={floatForm.amount}
                  onChange={(e) => setFloatForm({ ...floatForm, amount: e.target.value })}
                  placeholder="Enter amount"
                  style={styles.input}
                  required
                  min="1"
                />
              </div>
              <div style={styles.formGroup}>
                <label>Reference</label>
                <input
                  type="text"
                  value={floatForm.reference}
                  onChange={(e) => setFloatForm({ ...floatForm, reference: e.target.value })}
                  placeholder="Reference number"
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label>Description</label>
                <textarea
                  value={floatForm.description}
                  onChange={(e) => setFloatForm({ ...floatForm, description: e.target.value })}
                  placeholder="Description"
                  style={{ ...styles.input, minHeight: "60px" }}
                />
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowFloatModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={processing} 
                  style={{
                    ...styles.submitBtn,
                    backgroundColor: floatForm.type === "allocate" ? "#10b981" : "#ef4444"
                  }}
                >
                  {processing ? "Processing..." : floatForm.type === "allocate" ? "Allocate Float" : "Recall Float"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inter-Branch Transfer Modal */}
      {showTransferModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>🔄 Inter-Branch Transfer</h2>
            <form onSubmit={handleCreateTransfer}>
              <div style={styles.formGroup}>
                <label>From Branch *</label>
                <select
                  value={transferForm.fromBranchId}
                  onChange={(e) => setTransferForm({ ...transferForm, fromBranchId: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="">Select Source Branch</option>
                  {branches.filter(b => b.isActive).map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.code} - {branch.name} ({formatCurrency(branch.currentBalance)})
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label>To Branch *</label>
                <select
                  value={transferForm.toBranchId}
                  onChange={(e) => setTransferForm({ ...transferForm, toBranchId: e.target.value })}
                  style={styles.input}
                  required
                >
                  <option value="">Select Destination Branch</option>
                  {branches.filter(b => b.isActive && b.id !== parseInt(transferForm.fromBranchId)).map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.code} - {branch.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label>Amount (₦) *</label>
                <input
                  type="number"
                  value={transferForm.amount}
                  onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                  placeholder="Enter amount"
                  style={styles.input}
                  required
                  min="1"
                />
              </div>
              <div style={styles.formGroup}>
                <label>Description</label>
                <textarea
                  value={transferForm.description}
                  onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })}
                  placeholder="Reason for transfer"
                  style={{ ...styles.input, minHeight: "60px" }}
                />
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowTransferModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={processing} style={styles.submitBtn}>
                  {processing ? "Creating..." : "Create Transfer Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Journal Entry Modal */}
      {showJournalModal && selectedBranch && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxWidth: "700px" }}>
            <h2 style={styles.modalTitle}>📝 Create Journal Entry - {selectedBranch.name}</h2>
            <form onSubmit={handleCreateJournalEntry}>
              <div style={styles.formGroup}>
                <label>Description *</label>
                <input
                  type="text"
                  value={journalForm.description}
                  onChange={(e) => setJournalForm({ ...journalForm, description: e.target.value })}
                  placeholder="Journal entry description"
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label>Reference</label>
                <input
                  type="text"
                  value={journalForm.reference}
                  onChange={(e) => setJournalForm({ ...journalForm, reference: e.target.value })}
                  placeholder="Reference number"
                  style={styles.input}
                />
              </div>
              
              <h4 style={{ marginTop: "15px", marginBottom: "10px" }}>Entry Lines</h4>
              <table style={{ ...styles.table, marginBottom: "10px" }}>
                <thead>
                  <tr>
                    <th style={styles.th}>Account</th>
                    <th style={styles.th}>Debit (₦)</th>
                    <th style={styles.th}>Credit (₦)</th>
                    <th style={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {journalForm.lines.map((line, index) => (
                    <tr key={index}>
                      <td style={styles.td}>
                        <select
                          value={line.accountCode}
                          onChange={(e) => updateJournalLine(index, "accountCode", e.target.value)}
                          style={{ ...styles.input, padding: "6px" }}
                        >
                          <option value="">Select Account</option>
                          {branchLedger.map(account => (
                            <option key={account.id} value={account.accountCode}>
                              {account.accountCode} - {account.accountName}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          value={line.debit}
                          onChange={(e) => updateJournalLine(index, "debit", e.target.value)}
                          placeholder="0"
                          style={{ ...styles.input, padding: "6px", width: "100px" }}
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          value={line.credit}
                          onChange={(e) => updateJournalLine(index, "credit", e.target.value)}
                          placeholder="0"
                          style={{ ...styles.input, padding: "6px", width: "100px" }}
                        />
                      </td>
                      <td style={styles.td}>
                        {journalForm.lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeJournalLine(index)}
                            style={{ ...styles.smallBtn, backgroundColor: "#ef4444", padding: "4px 8px" }}
                          >
                            ×
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: "#f3f4f6" }}>
                    <td style={{ ...styles.td, fontWeight: "600" }}>Totals</td>
                    <td style={{ ...styles.td, fontWeight: "600", color: "#10b981" }}>
                      {formatCurrency(journalForm.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0))}
                    </td>
                    <td style={{ ...styles.td, fontWeight: "600", color: "#ef4444" }}>
                      {formatCurrency(journalForm.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0))}
                    </td>
                    <td style={styles.td}></td>
                  </tr>
                </tfoot>
              </table>
              <button type="button" onClick={addJournalLine} style={{ ...styles.smallBtn, marginBottom: "15px" }}>
                + Add Line
              </button>

              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowJournalModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={processing} style={styles.submitBtn}>
                  {processing ? "Creating..." : "Create Entry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f3f4f6",
    padding: "20px"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "10px"
  },
  backBtn: {
    padding: "10px 20px",
    backgroundColor: "#374151",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px"
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#1f2937",
    margin: 0
  },
  headerActions: {
    display: "flex",
    gap: "10px"
  },
  createBtn: {
    padding: "10px 20px",
    backgroundColor: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600"
  },
  transferBtn: {
    padding: "10px 20px",
    backgroundColor: "#6366f1",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600"
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px",
    marginBottom: "20px"
  },
  summaryCard: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: "15px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  },
  summaryIcon: {
    fontSize: "32px",
    width: "50px",
    height: "50px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f3f4f6",
    borderRadius: "12px"
  },
  summaryContent: {},
  summaryValue: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1f2937"
  },
  summaryLabel: {
    fontSize: "12px",
    color: "#6b7280"
  },
  tabs: {
    display: "flex",
    gap: "5px",
    marginBottom: "20px",
    flexWrap: "wrap",
    backgroundColor: "white",
    padding: "5px",
    borderRadius: "12px"
  },
  tab: {
    padding: "10px 20px",
    backgroundColor: "transparent",
    color: "#6b7280",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s"
  },
  tabActive: {
    backgroundColor: "#10b981",
    color: "white"
  },
  content: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
  },
  branchGrid: {
    display: "flex",
    flexDirection: "row",
    gap: "15px",
    overflowX: "auto",
    paddingBottom: "10px"
  },
  branchListContainer: {
    overflowX: "auto"
  },
  branchTable: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "800px"
  },
  branchTh: {
    textAlign: "left",
    padding: "12px 16px",
    backgroundColor: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "12px",
    fontWeight: "600",
    color: "#6b7280",
    textTransform: "uppercase"
  },
  branchTd: {
    padding: "16px",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "14px",
    color: "#374151"
  },
  branchRow: {
    cursor: "pointer",
    transition: "background-color 0.2s"
  },
  branchRowSelected: {
    backgroundColor: "#ecfdf5"
  },
  tableActionBtn: {
    padding: "6px 12px",
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500"
  },
  branchCard: {
    backgroundColor: "#f9fafb",
    borderRadius: "12px",
    padding: "15px",
    border: "2px solid transparent",
    cursor: "pointer",
    transition: "all 0.2s",
    flex: "0 0 auto",
    width: "220px"
  },
  branchCardSelected: {
    borderColor: "#10b981",
    backgroundColor: "#ecfdf5"
  },
  headOfficeCard: {
    background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
  },
  branchHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px"
  },
  branchCode: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#6b7280",
    backgroundColor: "#e5e7eb",
    padding: "4px 8px",
    borderRadius: "4px"
  },
  hqBadge: {
    marginLeft: "8px",
    fontSize: "10px",
    fontWeight: "700",
    color: "#92400e",
    backgroundColor: "#fef3c7",
    padding: "2px 6px",
    borderRadius: "4px"
  },
  statusBadge: {
    fontSize: "11px",
    fontWeight: "600",
    padding: "4px 8px",
    borderRadius: "12px"
  },
  branchName: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1f2937",
    margin: "0 0 5px 0"
  },
  branchLocation: {
    fontSize: "13px",
    color: "#6b7280",
    margin: "0 0 15px 0"
  },
  branchStats: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "10px",
    marginBottom: "15px"
  },
  branchStat: {
    textAlign: "center"
  },
  statLabel: {
    display: "block",
    fontSize: "11px",
    color: "#6b7280"
  },
  statValue: {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#1f2937"
  },
  managerInfo: {
    fontSize: "12px",
    color: "#6b7280",
    margin: "0 0 15px 0"
  },
  branchActions: {
    display: "flex",
    gap: "8px"
  },
  actionBtn: {
    flex: 1,
    padding: "8px",
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500"
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px"
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse"
  },
  th: {
    textAlign: "left",
    padding: "12px",
    backgroundColor: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "12px",
    fontWeight: "600",
    color: "#6b7280",
    textTransform: "uppercase"
  },
  td: {
    padding: "12px",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "14px",
    color: "#374151"
  },
  smallBtn: {
    padding: "6px 12px",
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "500"
  },
  agentBadge: {
    marginLeft: "8px",
    fontSize: "10px",
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    padding: "2px 6px",
    borderRadius: "4px"
  },
  roleBadge: {
    fontSize: "11px",
    backgroundColor: "#e0e7ff",
    color: "#4338ca",
    padding: "4px 8px",
    borderRadius: "4px"
  },
  typeBadge: {
    fontSize: "11px",
    padding: "4px 8px",
    borderRadius: "4px",
    fontWeight: "500"
  },
  journalEntry: {
    backgroundColor: "#f9fafb",
    borderRadius: "8px",
    padding: "15px",
    marginBottom: "15px"
  },
  journalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px"
  },
  journalDate: {
    marginLeft: "15px",
    fontSize: "12px",
    color: "#6b7280"
  },
  journalStatus: {
    fontSize: "11px",
    backgroundColor: "#d1fae5",
    color: "#065f46",
    padding: "4px 8px",
    borderRadius: "4px"
  },
  journalDesc: {
    fontSize: "13px",
    color: "#374151",
    margin: 0
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    padding: "20px"
  },
  modal: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "25px",
    width: "100%",
    maxWidth: "500px",
    maxHeight: "90vh",
    overflow: "auto"
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "20px",
    color: "#1f2937"
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "15px"
  },
  formGroup: {
    marginBottom: "15px"
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    fontSize: "14px",
    boxSizing: "border-box"
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "20px",
    paddingTop: "15px",
    borderTop: "1px solid #e5e7eb"
  },
  cancelBtn: {
    padding: "10px 20px",
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px"
  },
  submitBtn: {
    padding: "10px 20px",
    backgroundColor: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600"
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
    fontSize: "16px",
    color: "#6b7280"
  }
};
